import * as React from 'react';
import { commonStyles } from 'src/Library/CommonStyles';
import { StylesProps } from 'src/Library/Types';
import withStyles from 'react-jss';
import { POLPODPairData, SearchResult, RateType } from './TypesInDiscoverRates/SearchResult';
import { Ports } from 'src/components/common/Types/CommonTypes';
import classNames from 'classnames';
import Tooltip from 'antd/lib/tooltip';
import Spin from 'antd/lib/spin';
import { GridView } from './DiscoverRateUtils';
import { EventCategories, sendEvent } from 'src/utils/gaService';
const noRatesAvailableSrc = require('src/assets/images/no_rates_available.png');

interface IProps extends StylesProps<ReturnType<typeof GridBoxStyles>> {
  polPodPairData: POLPODPairData;
  fieldStyles: {
    gridBox: {
      height: number;
      width: string;
    },
    noRates: {
      image: {
        height: string;
        width: string;
      },
      font: string;
      marginTop: string;
    },
    ratesCountLabel: string;
    carrierNameLabel: string;
  };
  pol: Ports;
  pod: Ports;
  showListView: (data) => void;
}

const GridBoxStyles = (theme) => ({
  ...commonStyles(theme),
  gridBox: {
    backgroundColor: '#FFFFFF',
    border: '3px solid #FFFFFF',
    borderRadius: '5px',
    '&:hover': {
      boxShadow: '2px 2px 8px 0px rgba(0,0,0,0.1)',
    },
  },
  carrierName: {
    textAlign: 'center',
    opacity: 0.58,
    color: '#333333',
  },
  gridBoxWrapper: {
    paddingRight: '8px',
    flex: '0 0 auto',
    paddingTop: '8px',
    cursor: 'pointer',
    marginRight: '10px',
    marginBottom: '10px',
  }
});

const getRateTypeCount = (searchResults: SearchResult[], type) => {
  return searchResults?.filter((item) => item.type === type)?.length;
}

const GridBox = React.memo((props: IProps) => {
  const { 
    polPodPairData,
    fieldStyles,
    classes,
    pol,
    pod,
    showListView,
  } = props;
  const data = polPodPairData?.data;
  const showLoader = polPodPairData?.isLoading;
  const minFreightSearchResult = polPodPairData?.minFreightSearchResult;
  const ratesCountStyle = data && {
    backgroundColor: data.searchResults?.length
      ? (minFreightSearchResult?.type === 'BUY' ? '#EEF6F1' : '#FFF5DB')
      : '#EFEFEF',
    color: data.searchResults?.length ? '#006EC3' : '#333333',
  };

  const handleListView = () => {
    sendEvent(
      EventCategories.DISCOVER_RATES_SEARCH_FF,
      'Grid Box clicked',
    );
    if (!data?.searchResults?.length || showLoader) return;
    showListView({
      searchResults: data.searchResults,
      viewType: GridView.LIST,
      pol: {
        ...pol,
        id: pol.dbId
      },
      pod: {
        ...pod,
        id: pod.dbId
      },
    });
  }

  const renderRateFoundContent = (): React.ReactElement => {
    return <React.Fragment>
      <div
        className={classes.ellipses}
        style={{
          textAlign: 'center',
          color: '#111111',
          fontSize: '16px',  
        }}
      >
        <span
          style={{
            fontSize: '10px',
            lineHeight: '22px',
            verticalAlign: 'top'
          }}
        >
          $
        </span>
        <span>
          {Math.round(minFreightSearchResult?.totalFreight?.amount ?? 0) ?? '-' }
        </span>
      </div>
    </React.Fragment>
  };

  const renderRateNotFoundContnet = () => {
    return <div
      className={classes.flexRow}
      style={{
        textAlign: 'center',
        marginTop: fieldStyles.noRates.marginTop,
      }}
    >
      <div
        style={{
          marginRight: '5px'
        }}
      >
        <img
          src={noRatesAvailableSrc}
          style={{
            ...fieldStyles.noRates.image,
            marginTop: '-4px'
          }}
        />
      </div>
      <div
        style={{
          fontSize: fieldStyles.noRates.font,
          color: '#CCCCCC'
        }}>
          No Rates Available
        </div>
    </div>;
  }

  const renderCarrier = (minFreightSearchResult: SearchResult): React.ReactElement => {
    return <Tooltip
      mouseEnterDelay={0.5}
      title={minFreightSearchResult?.carrier?.name}
    >
      <div
        className={classNames(classes.ellipses, classes.carrierName)}
        style={{
          fontSize: fieldStyles.carrierNameLabel,
        }}
      >
        {minFreightSearchResult?.carrier?.name}
      </div>
    </Tooltip>;
  }

  const renderRatesCount = (): React.ReactElement => {
    return <Tooltip
      mouseEnterDelay={0.5}
      title={<React.Fragment>
        <div style={{ fontSize: '12px' }}>FAK Rates : {getRateTypeCount(data?.searchResults, 'FAK')}</div>
        <div style={{ fontSize: '12px' }}>Buy Rates : {getRateTypeCount(data?.searchResults, 'BUY')}</div>
      </React.Fragment>}
    >
      <div
        style={{
          borderRadius: '2px',
          textAlign: 'center',
          ...ratesCountStyle,
        }}
      >
        <span
          style={{
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {data?.searchResults?.length}
        </span>
        <span
          style={{
            fontSize: fieldStyles.ratesCountLabel,
            marginLeft: '3px'
          }}
        >
          Rates
        </span>
      </div>
    </Tooltip>;
  }

  return <div
    className={classes.gridBoxWrapper}
    style={{
      width: fieldStyles.gridBox.width,
    }}
    key={`${pod.dbId}-${pol.dbId}`}
    onClick={handleListView}
  >
    <div
      className={classNames(classes.flexColumn, classes.gridBox)}
      style={{
        height: `${fieldStyles.gridBox.height}px`,
      }}
    >
      {(showLoader || !data?.searchResults) && <Spin
        size="small"
        style={{
          position: 'relative',
          top: `${Math.floor(fieldStyles.gridBox.height/2) - 10}px`
        }}
      />}
      {!showLoader && data?.searchResults && <React.Fragment>
        {renderRatesCount()}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            border: '3px solid #FFFFFF',
            borderRadius: '2px',
          }}
        >
          {
            data?.searchResults?.length
              ? renderRateFoundContent()
              : renderRateNotFoundContnet()
          }
          {
            minFreightSearchResult?.carrier && renderCarrier(minFreightSearchResult)
          }
        </div>
      </React.Fragment>}
    </div>
  </div>
});

export default withStyles(GridBoxStyles)(GridBox);